import React, { useState, useRef, useEffect } from "react";
import { FaSortUp, FaSortDown, FaArrowLeft, FaTimes, FaHashtag, FaExclamationTriangle, FaTags, FaFileCode, FaClock, FaComment, FaDatabase, FaFilePdf, FaFileExcel, FaUser, FaDesktop, FaSignOutAlt, FaNetworkWired } from "react-icons/fa";
import api from "../../../services/AxiosConnection";
import useAuth from "../../../hooks/useAuth";
import { Vortex } from "react-loader-spinner";
import DownloadSystemLogsPDF from "../../../utils/DownloadSystemLogsPDF";
import DownloadSystemLogsExcel from "../../../utils/DownloadSystemLogsExcel";
import DownloadUserLogsPDF from "../../../utils/DownloadUserLogsPDF";
import DownloadUserLogsExcel from "../../../utils/DownloadUserLogsExcel";
function LogSearch() {
  const { user, selectedSite, selectedSiteName } = useAuth();
  const [activeView, setActiveView] = useState(null); // 'user' or 'system'
  const [showSortPopup, setShowSortPopup] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [timePeriod, setTimePeriod] = useState("7");
  const [sortOption, setSortOption] = useState("");
  const [sortDirection, setSortDirection] = useState("asc");
  const [systemLogs, setSystemLogs] = useState([]);
  const [userLogs, setUserLogs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [exportLoading, setExportLoading] = useState(false);
  const [selectedLog, setSelectedLog] = useState(null);
  const [showLogDetail, setShowLogDetail] = useState(false);
  const sortPopupRef = useRef(null);
  const popupRef = useRef(null);
  // Handle clicks outside the sort popup
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (sortPopupRef.current && !sortPopupRef.current.contains(event.target)) {
        setShowSortPopup(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);
  // Prevent background scrolling when popup is open
  useEffect(() => {
    if (showLogDetail) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'auto';
    }
    return () => {
      document.body.style.overflow = 'auto';
    };
  }, [showLogDetail]);
  // Handle clicks outside the popup
  useEffect(() => {
    const handleClickOutsidePopup = (event) => {
      if (popupRef.current && !popupRef.current.contains(event.target) && showLogDetail) {
        closeLogDetail();
      }
    };
    document.addEventListener("mousedown", handleClickOutsidePopup);
    return () => {
      document.removeEventListener("mousedown", handleClickOutsidePopup);
    };
  }, [showLogDetail]);
  // Fetch system logs when view changes or time period changes
  useEffect(() => {
    if (activeView === "system" && selectedSite) {
      fetchSystemLogs();
    } else if (activeView === "user") {
      fetchUserLogs();
    }
  }, [activeView, timePeriod, selectedSite]);
  const fetchSystemLogs = async () => {
    setLoading(true);
    try {
      const response = await api.get(
        `/control-panel/getSystemLogsByDate?siteDb=${selectedSite}&timePeriod=${timePeriod}`
      );
      if (response.data && response.data.logs) {
        setSystemLogs(response.data.logs);
      }
    } catch (error) {
      console.error("Error fetching system logs:", error);
    } finally {
      setLoading(false);
    }
  };
  const fetchUserLogs = async () => {
    setLoading(true);
    try {
      // Calculate date range based on time period
      const toDate = new Date();
      const fromDate = new Date();
      fromDate.setDate(toDate.getDate() - parseInt(timePeriod));
      
      // Format dates as YYYY-MM-DD
      const formattedFromDate = fromDate.toISOString().split('T')[0];
      const formattedToDate = toDate.toISOString().split('T')[0];
      
      const response = await api.get(
        `/control-panel/getUserLogs?userId=${user.id}&fromDate=${formattedFromDate}&toDate=${formattedToDate}`
      );
      if (response.data && response.data.logs) {
        setUserLogs(response.data.logs);
      }
    } catch (error) {
      console.error("Error fetching user logs:", error);
    } finally {
      setLoading(false);
    }
  };
  const handleCardClick = (viewType) => {
    setActiveView(viewType);
    setShowSortPopup(false);
    setSearchTerm("");
    setSortOption("");
    setSortDirection("asc");
    
    if (viewType === "system") {
      fetchSystemLogs();
    } else if (viewType === "user") {
      fetchUserLogs();
    }
  };
  const handleBackToCards = () => {
    setActiveView(null);
    setShowSortPopup(false);
    setSystemLogs([]);
    setUserLogs([]);
  };
  const handleSortClick = (option) => {
    if (sortOption === option) {
      // Toggle direction if same option is clicked
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      setSortOption(option);
      setSortDirection("asc");
    }
    setShowSortPopup(false);
  };
  const handleLogRowClick = (log, index) => {
    setSelectedLog({ ...log, index });
    setShowLogDetail(true);
  };
  const closeLogDetail = () => {
    setShowLogDetail(false);
    setSelectedLog(null);
  };
  const handleExportPDF = async () => {
    setExportLoading(true);
    try {
      // Format logs with proper dates before exporting
      const formattedLogs = filteredLogs.map(log => {
        if (activeView === "system") {
          return {
            ...log,
            LogTimestamp: formatDate(log.LogTimestamp)
          };
        } else {
          return {
            ...log,
            LoginTime: formatDate(log.LoginTime),
            LogoutTime: log.LogoutTime ? formatDate(log.LogoutTime) : null
          };
        }
      });

      if (activeView === "system") {
        await DownloadSystemLogsPDF(
          selectedSite,
          selectedSiteName || "Unknown Site",
          formattedLogs,
          timePeriod,
          setExportLoading
        );
      } else if (activeView === "user") {
        await DownloadUserLogsPDF(
          selectedSite,
          selectedSiteName || "Unknown Site",
          formattedLogs,
          timePeriod,
          setExportLoading
        );
      }
    } catch (error) {
      console.error("Error exporting PDF:", error);
      alert("Failed to export PDF. Please try again.");
    } finally {
      setExportLoading(false);
    }
  };
  const handleExportExcel = async () => {
    setExportLoading(true);
    try {
      // Format logs with proper dates before exporting
      const formattedLogs = filteredLogs.map(log => {
        if (activeView === "system") {
          return {
            ...log,
            LogTimestamp: formatDate(log.LogTimestamp)
          };
        } else {
          return {
            ...log,
            LoginTime: formatDate(log.LoginTime),
            LogoutTime: log.LogoutTime ? formatDate(log.LogoutTime) : null
          };
        }
      });

      if (activeView === "system") {
        await DownloadSystemLogsExcel(
          selectedSite,
          selectedSiteName || "Unknown Site",
          formattedLogs,
          timePeriod,
          setExportLoading
        );
      } else if (activeView === "user") {
        await DownloadUserLogsExcel(
          selectedSite,
          selectedSiteName || "Unknown Site",
          formattedLogs,
          timePeriod,
          setExportLoading
        );
      }
    } catch (error) {
      console.error("Error exporting Excel:", error);
      alert("Failed to export Excel. Please try again.");
    } finally {
      setExportLoading(false);
    }
  };
  // Calculate browsing time from login and logout time
  const calculateBrowsingTime = (loginTime, logoutTime) => {
    if (!logoutTime) {
      return "Active";
    }
    
    const login = new Date(loginTime);
    const logout = new Date(logoutTime);
    const diffMs = logout - login;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHrs = Math.floor(diffMins / 60);
    const remainingMins = diffMins % 60;
    
    return `${diffHrs}h ${remainingMins}m`;
  };
  // Get log level color class
  const getLogLevelColorClass = (logLevel) => {
    switch (logLevel) {
      case 'EVENT': return 'bg-green-100 text-green-800';
      case 'SYSTEM': return 'bg-yellow-100 text-yellow-800';
      default: return 'bg-blue-100 text-blue-800';
    }
  };
  // Get category color class
  const getCategoryColorClass = (category) => {
    if (!category) return 'bg-gray-100 text-gray-800';
    switch (category.toLowerCase()) {
      case 'uploader': return 'bg-purple-100 text-purple-800';
      case 'proxy': return 'bg-indigo-100 text-indigo-800';
      case 'plc': return 'bg-blue-100 text-blue-800';
      case 'database': return 'bg-cyan-100 text-cyan-800';
      case 'restart': return 'bg-red-100 text-red-800';
      case 'connectivity': return 'bg-green-100 text-green-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };
  // Get status color class
  const getStatusColorClass = (isActive) => {
    return isActive ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800';
  };
  // Format source script name for display
  const formatSourceScript = (script) => {
    if (!script) return '';
    // Remove .js extension
    let formatted = script.replace(/\.js$/, '');
    // Special cases
    if (formatted === 'HabdStatus') return 'HABD Status';
    if (formatted === 'TemperatureStatus') return 'Temperature Status';
    if (formatted === 'ProxyStatus') return 'Proxy Status';
    if (formatted === 'PlcStatus') return 'PLC Status';
    if (formatted === 'InternetConnectivity') return 'Internet Connectivity';
    if (formatted === 'RestartStatus') return 'Restart Status';
    // Default: insert space before capital letters and capitalize first letter
    formatted = formatted.replace(/([A-Z])/g, ' $1')
      .replace(/^./, str => str.toUpperCase())
      .trim();
    return formatted;
  };
  // Format date as "28 Aug 2025 12:38 PM" using UTC
  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    
    // Get UTC date components
    const day = date.getUTCDate();
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    const month = months[date.getUTCMonth()];
    const year = date.getUTCFullYear();
    
    // Get UTC time components
    let hours = date.getUTCHours();
    const minutes = date.getUTCMinutes().toString().padStart(2, '0');
    const ampm = hours >= 12 ? 'PM' : 'AM';
    hours = hours % 12 || 12; // Convert to 12-hour format
    
    return `${day} ${month} ${year} ${hours}:${minutes} ${ampm}`;
  };
  // Format category with first letter uppercase
  const formatCategory = (category) => {
    if (!category) return 'N/A';
    return category.charAt(0).toUpperCase() + category.slice(1).toLowerCase();
  };
  // Format additional data for display
  const formatAdditionalData = (data) => {
    if (!data) return 'N/A';
    try {
      const parsed = JSON.parse(data);
      return JSON.stringify(parsed, null, 2);
    } catch (e) {
      return data;
    }
  };
  // Sort logs based on current sort option and direction
  const sortedSystemLogs = [...systemLogs].sort((a, b) => {
    if (!sortOption) return 0;
    let valueA, valueB;
    switch (sortOption) {
      case "category":
        valueA = a.Category || "";
        valueB = b.Category || "";
        break;
      case "date":
        valueA = new Date(a.LogTimestamp);
        valueB = new Date(b.LogTimestamp);
        break;
      case "logLevel":
        valueA = a.LogLevel || "";
        valueB = b.LogLevel || "";
        break;
      default:
        return 0;
    }
    if (sortDirection === "asc") {
      return valueA > valueB ? 1 : -1;
    } else {
      return valueA < valueB ? 1 : -1;
    }
  });
  // Sort user logs based on current sort option and direction
  const sortedUserLogs = [...userLogs].sort((a, b) => {
    if (!sortOption) return 0;
    let valueA, valueB;
    switch (sortOption) {
      case "loginTime":
        valueA = new Date(a.LoginTime);
        valueB = new Date(b.LoginTime);
        break;
      case "user":
        valueA = a.username || "";
        valueB = b.username || "";
        break;
      default:
        return 0;
    }
    if (sortDirection === "asc") {
      return valueA > valueB ? 1 : -1;
    } else {
      return valueA < valueB ? 1 : -1;
    }
  });
  // Filter system logs based on search term
  const filteredSystemLogs = sortedSystemLogs.filter(log => {
    if (!searchTerm) return true;
    const searchLower = searchTerm.toLowerCase();
    return (
      (log.LogLevel && log.LogLevel.toLowerCase().includes(searchLower)) ||
      (log.SourceScript && log.SourceScript.toLowerCase().includes(searchLower)) ||
      (log.Category && log.Category.toLowerCase().includes(searchLower)) ||
      (log.Message && log.Message.toLowerCase().includes(searchLower)) ||
      (log.AdditionalData && log.AdditionalData.toLowerCase().includes(searchLower))
    );
  });
  // Filter user logs based on search term
  const filteredUserLogs = sortedUserLogs.filter(log => {
    if (!searchTerm) return true;
    const searchLower = searchTerm.toLowerCase();
    return (
      (log.username && log.username.toLowerCase().includes(searchLower)) ||
      (log.email && log.email.toLowerCase().includes(searchLower)) ||
      (log.IPAddress && log.IPAddress.toLowerCase().includes(searchLower)) ||
      (log.UserAgent && log.UserAgent.toLowerCase().includes(searchLower))
    );
  });
  // Determine which logs to display based on active view
  const filteredLogs = activeView === "system" ? filteredSystemLogs : filteredUserLogs;
  const renderSortOptions = () => {
    if (activeView === "user") {
      return (
        <>
          <div
            className="px-3 py-2 hover:bg-gray-100 cursor-pointer flex justify-between items-center text-sm"
            onClick={() => handleSortClick("loginTime")}
          >
            <span>Sort by Login Time</span>
            {sortOption === "loginTime" && (
              <span>{sortDirection === "asc" ? <FaSortUp /> : <FaSortDown />}</span>
            )}
          </div>
          <div
            className="px-3 py-2 hover:bg-gray-100 cursor-pointer flex justify-between items-center text-sm"
            onClick={() => handleSortClick("user")}
          >
            <span>Sort by User</span>
            {sortOption === "user" && (
              <span>{sortDirection === "asc" ? <FaSortUp /> : <FaSortDown />}</span>
            )}
          </div>
        </>
      );
    } else if (activeView === "system") {
      return (
        <>
          <div
            className="px-3 py-2 hover:bg-gray-100 cursor-pointer flex justify-between items-center text-sm"
            onClick={() => handleSortClick("category")}
          >
            <span>Sort by Category</span>
            {sortOption === "category" && (
              <span>{sortDirection === "asc" ? <FaSortUp /> : <FaSortDown />}</span>
            )}
          </div>
          <div
            className="px-3 py-2 hover:bg-gray-100 cursor-pointer flex justify-between items-center text-sm"
            onClick={() => handleSortClick("date")}
          >
            <span>Sort by Date</span>
            {sortOption === "date" && (
              <span>{sortDirection === "asc" ? <FaSortUp /> : <FaSortDown />}</span>
            )}
          </div>
          <div
            className="px-3 py-2 hover:bg-gray-100 cursor-pointer flex justify-between items-center text-sm"
            onClick={() => handleSortClick("logLevel")}
          >
            <span>Sort by Log Level</span>
            {sortOption === "logLevel" && (
              <span>{sortDirection === "asc" ? <FaSortUp /> : <FaSortDown />}</span>
            )}
          </div>
        </>
      );
    }
  };
  if (activeView) {
    return (
      <div className="p-2 sm:p-4 bg-gray-50 min-h-screen">
        {/* Header Section */}
        <div className="bg-blue-50 p-2 sm:p-3 rounded-lg mb-3 sm:mb-4 shadow-sm">
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <button
                onClick={handleBackToCards}
                className="mr-2 text-blue-600 hover:text-blue-800 flex items-center"
              >
                <FaArrowLeft className="h-3 w-3 mr-1" />
              </button>
              <div>
                <h2 className="text-xs sm:text-sm font-bold text-blue-800 flex items-center">
                  {activeView === "user" ? `User Logs (${filteredUserLogs.length})` : `System Logs (${filteredSystemLogs.length})`}
                </h2>
                <p className="text-xs text-gray-600">
                  {activeView === "user"
                    ? "Viewing and searching user activity logs"
                    : "Viewing and searching system event logs"}
                </p>
              </div>
            </div>
            <div className="flex space-x-2">
              <button
                onClick={handleExportPDF}
                className="text-red-600 hover:text-red-800 flex items-center"
                title="Export to PDF"
                disabled={exportLoading || filteredLogs.length === 0}
              >
                <FaFilePdf className="h-4 w-4" />
              </button>
              <button
                onClick={handleExportExcel}
                className="text-green-600 hover:text-green-800 flex items-center"
                title="Export to Excel"
                disabled={exportLoading || filteredLogs.length === 0}
              >
                <FaFileExcel className="h-4 w-4" />
              </button>
            </div>
          </div>
        </div>
        
        {/* Controls Section */}
        <div className="bg-white p-2 sm:p-3 rounded-lg shadow-sm mb-3 sm:mb-4">
          <div className="flex flex-col sm:flex-row gap-2 w-full">
            {/* Search Bar */}
            <div className="w-full sm:w-1/2">
              <input
                type="text"
                placeholder="Search logs..."
                className="w-full px-2 py-1 text-xs border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
            {/* Time Period Dropdown */}
            <div className="w-full sm:w-1/4">
              <select
                className="w-full px-2 py-1 text-xs border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
                value={timePeriod}
                onChange={(e) => setTimePeriod(e.target.value)}
              >
                <option value="7">Last 7 Days</option>
                <option value="15">Last 15 Days</option>
                <option value="30">Last 30 Days</option>
              </select>
            </div>
            {/* Sort Button */}
            <div className="w-full sm:w-1/4 relative" ref={sortPopupRef}>
              <button
                className="w-full bg-gray-100 hover:bg-gray-200 text-gray-800 text-xs font-medium px-2 py-1 rounded transition flex items-center justify-center"
                onClick={() => setShowSortPopup(!showSortPopup)}
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4h13M3 8h9m-9 4h6m4 0l4-4m0 0l4 4m-4-4v12" />
                </svg>
                Sort
              </button>
              {/* Sort Popup */}
              {showSortPopup && (
                <div className="absolute right-0 mt-1 w-48 bg-white rounded-md shadow-lg z-10 border border-gray-200">
                  <div className="py-1">
                    {renderSortOptions()}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
        
        {/* Logs Display Area */}
        <div className="bg-white p-2 sm:p-4 rounded-lg shadow-sm">
          <h3 className="text-xs sm:text-sm font-semibold text-gray-800 mb-2 sm:mb-3">
            {activeView === "user" ? "User Activity Logs" : "System Event Logs"}
          </h3>
          {loading ? (
            <div className="flex justify-center items-center h-32 sm:h-40">
              <Vortex
                visible={true}
                height={30}
                width={30}
                ariaLabel="vortex-loading"
                wrapperStyle={{}}
                wrapperClass="vortex-wrapper"
                colors={["#2563eb", "#1e40af", "#60a5fa", "#3b82f6", "#1d4ed8", "#93c5fd"]}
              />
            </div>
          ) : (
            <>
              {activeView === "system" && (
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200 table-fixed">
                    <thead className="bg-gray-50">
                      <tr>
                        <th scope="col" className="w-12 px-2 sm:px-3 py-1 sm:py-2 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Sr. No.
                        </th>
                        <th scope="col" className="w-24 px-2 sm:px-3 py-1 sm:py-2 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Log Level
                        </th>
                        <th scope="col" className="w-32 sm:w-40 px-2 sm:px-3 py-1 sm:py-2 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Source Script
                        </th>
                        <th scope="col" className="w-32 sm:w-40 px-2 sm:px-3 py-1 sm:py-2 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Timestamp
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {filteredSystemLogs.length > 0 ? (
                        filteredSystemLogs.map((log, index) => (
                          <tr
                            key={log.ID}
                            className="hover:bg-gray-50 cursor-pointer"
                            onClick={() => handleLogRowClick(log, index)}
                          >
                            <td className="w-12 px-2 sm:px-3 py-1 sm:py-2 whitespace-nowrap text-xs text-gray-500 text-center">
                              {index + 1}
                            </td>
                            <td className="w-24 px-2 sm:px-3 py-1 sm:py-2 whitespace-nowrap text-center">
                              <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${getLogLevelColorClass(log.LogLevel)}`}>
                                {log.LogLevel}
                              </span>
                            </td>
                            <td className="w-32 sm:w-40 px-2 sm:px-3 py-1 sm:py-2 text-xs text-gray-900 text-center truncate" title={formatSourceScript(log.SourceScript)}>
                              {formatSourceScript(log.SourceScript)}
                            </td>
                            <td className="w-32 sm:w-40 px-2 sm:px-3 py-1 sm:py-2 text-xs text-gray-500 text-center truncate" title={formatDate(log.LogTimestamp)}>
                              {formatDate(log.LogTimestamp)}
                            </td>
                          </tr>
                        ))
                      ) : (
                        <tr>
                          <td colSpan="5" className="px-2 sm:px-3 py-3 sm:py-4 text-center text-xs text-gray-500">
                            No logs found for the selected criteria.
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              )}
              
              {activeView === "user" && (
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200 table-fixed">
                    <thead className="bg-gray-50">
                      <tr>
                        <th scope="col" className="w-12 px-2 sm:px-3 py-1 sm:py-2 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Sr. No.
                        </th>
                        <th scope="col" className="w-32 px-2 sm:px-3 py-1 sm:py-2 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                          User
                        </th>
                        <th scope="col" className="w-32 sm:w-40 px-2 sm:px-3 py-1 sm:py-2 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Login Time
                        </th>
                        <th scope="col" className="w-32 sm:w-40 px-2 sm:px-3 py-1 sm:py-2 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Browsing Time
                        </th>
                        <th scope="col" className="w-24 px-2 sm:px-3 py-1 sm:py-2 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Status
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {filteredUserLogs.length > 0 ? (
                        filteredUserLogs.map((log, index) => (
                          <tr
                            key={log.ID}
                            className="hover:bg-gray-50 cursor-pointer"
                            onClick={() => handleLogRowClick(log, index)}
                          >
                            <td className="w-12 px-2 sm:px-3 py-1 sm:py-2 whitespace-nowrap text-xs text-gray-500 text-center">
                              {index + 1}
                            </td>
                            <td className="w-32 px-2 sm:px-3 py-1 sm:py-2 text-xs text-gray-900 text-center truncate" title={log.username}>
                              {log.username}
                            </td>
                            <td className="w-32 sm:w-40 px-2 sm:px-3 py-1 sm:py-2 text-xs text-gray-500 text-center truncate" title={formatDate(log.LoginTime)}>
                              {formatDate(log.LoginTime)}
                            </td>
                            <td className="w-32 sm:w-40 px-2 sm:px-3 py-1 sm:py-2 text-xs text-gray-500 text-center truncate">
                              {calculateBrowsingTime(log.LoginTime, log.LogoutTime)}
                            </td>
                            <td className="w-24 px-2 sm:px-3 py-1 sm:py-2 whitespace-nowrap text-center">
                              <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${getStatusColorClass(log.IsActive)}`}>
                                {log.IsActive ? "Active" : "Inactive"}
                              </span>
                            </td>
                          </tr>
                        ))
                      ) : (
                        <tr>
                          <td colSpan="5" className="px-2 sm:px-3 py-3 sm:py-4 text-center text-xs text-gray-500">
                            No user logs found for the selected criteria.
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              )}
            </>
          )}
        </div>
        
        {/* Log Detail Popup - Fixed position overlay */}
        {showLogDetail && selectedLog && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-2 sm:p-4">
            <div
              ref={popupRef}
              className="bg-white rounded-lg shadow-lg w-full max-w-md sm:max-w-lg md:max-w-2xl max-h-[70vh] flex flex-col border border-gray-200"
            >
              <div className="bg-gray-50 p-2 sm:p-3 border-b border-gray-200 flex justify-between items-center rounded-t-lg">
                <h3 className="text-sm sm:text-md font-semibold text-gray-800">
                  {activeView === "user" ? "User Log Details" : "System Log Details"}
                </h3>
                <button
                  onClick={closeLogDetail}
                  className="text-red-600 hover:text-red-800 transition-colors"
                >
                  <FaTimes className="h-4 w-4" />
                </button>
              </div>
              <div className="flex-1 overflow-y-auto p-2 sm:p-3 space-y-2 sm:space-y-3">
                {activeView === "user" ? (
                  <>
                    {/* User Log Details */}
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 sm:gap-3">
                      <div className="flex items-center">
                        <FaHashtag className="h-3 w-3 mr-1 text-blue-600" />
                        <span className="text-xs text-gray-500 font-medium w-24">Sr. No.:</span>
                        <span className="text-xs sm:text-sm font-medium truncate">{selectedLog.index + 1}</span>
                      </div>
                      <div className="flex items-center">
                        <FaUser className="h-3 w-3 mr-1 text-blue-600" />
                        <span className="text-xs text-gray-500 font-medium w-24">Username:</span>
                        <span className="text-xs sm:text-sm font-medium truncate">{selectedLog.username}</span>
                      </div>
                      <div className="flex items-center">
                        <FaDesktop className="h-3 w-3 mr-1 text-blue-600" />
                        <span className="text-xs text-gray-500 font-medium w-24">Status:</span>
                        <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${getStatusColorClass(selectedLog.IsActive)}`}>
                          {selectedLog.IsActive ? "Active" : "Inactive"}
                        </span>
                      </div>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 sm:gap-3">
                      <div className="flex items-center">
                        <FaClock className="h-3 w-3 mr-1 text-blue-600" />
                        <span className="text-xs text-gray-500 font-medium w-24">Login Time:</span>
                        <span className="text-xs sm:text-sm font-medium">{formatDate(selectedLog.LoginTime)}</span>
                      </div>
                      <div className="flex items-center">
                        <FaSignOutAlt className="h-3 w-3 mr-1 text-blue-600" />
                        <span className="text-xs text-gray-500 font-medium w-24">Logout Time:</span>
                        <span className="text-xs sm:text-sm font-medium">
                          {selectedLog.LogoutTime ? formatDate(selectedLog.LogoutTime) : "Still Active"}
                        </span>
                      </div>
                    </div>
                    <div className="flex items-center">
                      <FaNetworkWired className="h-3 w-3 mr-1 text-blue-600" />
                      <span className="text-xs text-gray-500 font-medium w-24">IP Address:</span>
                      <span className="text-xs sm:text-sm font-medium">{selectedLog.IPAddress}</span>
                    </div>
                    <div>
                      <div className="flex items-center mb-1">
                        <FaComment className="h-3 w-3 mr-1 text-blue-600" />
                        <p className="text-xs text-gray-500 font-medium">User Agent:</p>
                      </div>
                      <p className="text-xs bg-gray-50 p-2 rounded whitespace-pre-wrap border border-gray-200 max-h-32 overflow-y-auto">
                        {selectedLog.UserAgent}
                      </p>
                    </div>
                    <div>
                      <div className="flex items-center mb-1">
                        <FaDatabase className="h-3 w-3 mr-1 text-blue-600" />
                        <p className="text-xs text-gray-500 font-medium">Email:</p>
                      </div>
                      <p className="text-xs bg-gray-50 p-2 rounded whitespace-pre-wrap border border-gray-200">
                        {selectedLog.email}
                      </p>
                    </div>
                  </>
                ) : (
                  <>
                    {/* System Log Details */}
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 sm:gap-3">
                      <div className="flex items-center">
                        <FaHashtag className="h-3 w-3 mr-1 text-blue-600" />
                        <span className="text-xs text-gray-500 font-medium w-24">Sr. No.:</span>
                        <span className="text-xs sm:text-sm font-medium truncate">{selectedLog.index + 1}</span>
                      </div>
                      <div className="flex items-center">
                        <FaExclamationTriangle className="h-3 w-3 mr-1 text-blue-600" />
                        <span className="text-xs text-gray-500 font-medium w-24">Log Level:</span>
                        <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${getLogLevelColorClass(selectedLog.LogLevel)}`}>
                          {selectedLog.LogLevel}
                        </span>
                      </div>
                      <div className="flex items-center">
                        <FaTags className="h-3 w-3 mr-1 text-blue-600" />
                        <span className="text-xs text-gray-500 font-medium w-24">Category:</span>
                        <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${getCategoryColorClass(selectedLog.Category)}`}>
                          {formatCategory(selectedLog.Category)}
                        </span>
                      </div>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 sm:gap-3">
                      <div className="flex items-center">
                        <FaFileCode className="h-3 w-3 mr-1 text-blue-600" />
                        <span className="text-xs text-gray-500 font-medium w-24">Source Script:</span>
                        <span className="text-xs sm:text-sm font-medium truncate">{formatSourceScript(selectedLog.SourceScript)}</span>
                      </div>
                      <div className="flex items-center whitespace-nowrap">
                        <FaClock className="h-3 w-3 mr-1 text-blue-600" />
                        <span className="text-xs text-gray-500 font-medium w-24">Timestamp:</span>
                        <span className="text-xs sm:text-sm font-medium">{formatDate(selectedLog.LogTimestamp)}</span>
                      </div>
                    </div>
                    <div>
                      <div className="flex items-center mb-1">
                        <FaComment className="h-3 w-3 mr-1 text-blue-600" />
                        <p className="text-xs text-gray-500 font-medium">Message:</p>
                      </div>
                      <p className="text-xs bg-gray-50 p-2 rounded whitespace-pre-wrap border border-gray-200 max-h-32 overflow-y-auto">
                        {selectedLog.Message}
                      </p>
                    </div>
                    {selectedLog.AdditionalData && (
                      <div>
                        <div className="flex items-center mb-1">
                          <FaDatabase className="h-3 w-3 mr-1 text-blue-600" />
                          <p className="text-xs text-gray-500 font-medium">Additional Data:</p>
                        </div>
                        <p className="text-xs bg-gray-50 p-2 rounded whitespace-pre-wrap border border-gray-200 max-h-32 overflow-y-auto">
                          {formatAdditionalData(selectedLog.AdditionalData)}
                        </p>
                      </div>
                    )}
                  </>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }
  return (
    <div className="p-2 sm:p-4 bg-gray-50 min-h-screen">
      {/* Header Section */}
      <div className="bg-blue-50 p-3 sm:p-4 rounded-lg mb-3 sm:mb-4 shadow-sm">
        <h2 className="text-sm font-bold text-blue-800 mb-1">Log Search</h2>
        <p className="text-xs text-gray-600">
          Searching, viewing & backing up local and remote-based logs.
        </p>
      </div>
      
      {/* Actions Section */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 sm:gap-4">
        {/* User Logs */}
        <div
          className="bg-white p-3 sm:p-4 rounded-lg shadow-sm cursor-pointer hover:shadow-md transition"
          onClick={() => handleCardClick("user")}
        >
          <h3 className="text-sm font-semibold text-gray-800 mb-2">User Logs</h3>
          <p className="text-xs text-gray-600 mb-2">
            Track user activities and interactions to understand system usage patterns.
          </p>
          <div className="flex items-center text-blue-600">
            <span className="text-xs font-medium">View User Logs</span>
            <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3 ml-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </div>
        </div>
        
        {/* System Logs */}
        <div
          className="bg-white p-3 sm:p-4 rounded-lg shadow-sm cursor-pointer hover:shadow-md transition"
          onClick={() => handleCardClick("system")}
        >
          <h3 className="text-sm font-semibold text-gray-800 mb-2">System Logs</h3>
          <p className="text-xs text-gray-600 mb-2">
            Monitor system events and alerts to maintain system health and performance.
          </p>
          <div className="flex items-center text-blue-600">
            <span className="text-xs font-medium">View System Logs</span>
            <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3 ml-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </div>
        </div>
      </div>
    </div>
  );
}
export default LogSearch;